import { styled } from '@mui/material/styles';
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { appConfig } from '../../config/runtimeConfig';

// Widths the CDN is asked for. Rendered widths are rounded up to one of
// these so every card of a similar size shares a cached rendition.
const WIDTH_BUCKETS = [400, 600, 800, 1200, 1600, 2400];

// Used before the container is measured, including in server-rendered
// HTML. 800 covers a full-width card on a phone (~400 CSS px at 2x), the
// slow-network case this matters most for; wider layouts upgrade once
// measured, narrower ones just keep it rather than downloading again.
const INITIAL_WIDTH = 800;

// Beyond 2x the extra pixels aren't visible at card sizes but still cost
// bytes (a 3x phone would otherwise fetch 1.5x the data for no gain).
const MAX_PIXEL_RATIO = 2;

// The CDN's default WebP quality is 80; 75 is ~10-16% smaller with no
// visible difference at these sizes. Each filter must be its own
// `filters:` path segment - chained `filters:a():b()` is silently ignored.
const QUALITY = 75;

const bucketFor = (width: number) =>
  WIDTH_BUCKETS.find((bucket) => bucket >= width) ??
  WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1];

// The widest the image renders (CSS px) at viewports of `minWidth` and up,
// in ascending order - like an <img> `sizes` attribute, but for a background.
export type RenderedWidths = { minWidth: number; width: number }[];

// With `renderedWidths`, the background is chosen by CSS alone: a media query
// per step, each an image-set() of 1x/2x candidates. The browser picks
// one before downloading, so there's a single request per image on any
// screen, straight from the server HTML with no measuring. The plain url()
// before it is a fallback for browsers without image-set() - they skip the
// declaration they can't parse, and ones that can only fetch the last.
const ImageBox = styled('div', {
  shouldForwardProp: (prop) => prop !== 'renderedWidths' && prop !== 'cdnUrl',
})<{ renderedWidths?: RenderedWidths; cdnUrl: (width: number) => string }>(
  ({ renderedWidths, cdnUrl }) =>
    Object.fromEntries(
      (renderedWidths ?? []).map(({ minWidth, width }) => {
        const x1 = cdnUrl(bucketFor(width));
        const x2 = cdnUrl(bucketFor(width * MAX_PIXEL_RATIO));
        return [
          `@media (min-width:${minWidth}px)`,
          {
            backgroundImage: [
              `url("${x1}")`,
              `image-set(url("${x1}") 1x, url("${x2}") 2x)`,
            ],
          },
        ];
      })
    )
);

interface OwnProps {
  src: string;
  aspect: string;
  isSliced?: boolean;
  notCDN?: boolean;
  className?: string;
  borderRadius?: any;
  backgroundPosition?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  /**
   * The widest the image renders at each viewport width, when the layout
   * knows it. Picks the size in CSS instead of measuring in JS - see
   * ImageBox. Without it the image is measured after mount and upgraded if
   * the initial rendition is too small, which can cost a second download.
   */
  renderedWidths?: RenderedWidths;
}

const ratioMappings: { [key: string]: string } = {
  '32:9': '28%',
  '21:9': '36%',
  '16:9': '56.25%',
  '3:2': '66%',
  '4:3': '75%',
  '1:1': '100%',
};

const AspectImageLoader: React.FC<OwnProps> = (props) => {
  const aspectRef = useRef<HTMLDivElement | null>(null);

  const {
    aspect,
    src,
    isSliced,
    notCDN,
    className,
    borderRadius,
    onClick,
    backgroundPosition,
    renderedWidths,
  } = props;
  const sizedByCss = !!renderedWidths && !notCDN && !!src;

  const cdnUrl = useCallback(
    (width: number) =>
      `${appConfig.APP_CDN_BASE_URL}/${width}x0/filters:format(webp)/filters:quality(${QUALITY})/${appConfig.APP_STAGE}/${src}`,
    [src]
  );

  // The width of the rendition currently shown, so a re-measure only ever
  // upgrades - each card downloads its image once unless it needs bigger.
  const initialShown = useCallback(
    () =>
      src
        ? { width: INITIAL_WIDTH, url: cdnUrl(INITIAL_WIDTH) }
        : { width: 0, url: '' },
    [cdnUrl, src]
  );
  const [shown, setShown] = useState(initialShown);
  const url = shown.url;

  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = aspectRef.current;
    if (sizedByCss || !element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      setContainerWidth((current) => (width > current ? width : current));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [sizedByCss]);

  useEffect(() => {
    // New image: start again from the initial rendition (a no-op on mount).
    setShown((current) => {
      const next = initialShown();
      return current.url === next.url ? current : next;
    });
  }, [initialShown]);

  useEffect(() => {
    if (!src || containerWidth <= 0) return;

    const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const target = bucketFor(Math.ceil(containerWidth * pixelRatio));
    if (target <= shown.width) return;

    // Swap only once the bigger image has loaded, so the current one stays
    // up meanwhile instead of flashing to the grey placeholder.
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setShown({ width: target, url: image.src });
    };
    image.src = cdnUrl(target);

    return () => {
      cancelled = true;
      image.onload = null;
    };
  }, [cdnUrl, containerWidth, shown.width, src]);

  return (
    <ImageBox
      id="aspect-image"
      ref={aspectRef}
      className={className}
      renderedWidths={sizedByCss ? renderedWidths : undefined}
      cdnUrl={cdnUrl}
      onClick={onClick}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        paddingBottom: !isSliced
          ? ratioMappings[aspect]
          : `calc(${ratioMappings[aspect]} - 56%)`,
        backgroundImage: sizedByCss
          ? undefined
          : notCDN
            ? `url("${src}")`
            : `url("${url}")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: backgroundPosition
          ? backgroundPosition
          : 'center center',
        backgroundSize: '100%',
        backgroundColor: '#eee',
        position: 'relative',
        borderRadius: borderRadius || '0px',
        cursor: 'pointer',
      }}
    />
  );
};

export default AspectImageLoader;
